// common/components/fileUpload/FileUpload.tsx
import { SetStateAction } from 'react'
import { PairInterface } from '../../types/interfaces'
import classes from './FileUpload.module.sass'
import { useFileUpload } from './hooks/useFileUpload'
import { useSelector } from 'react-redux'
import { selectDarkTheme } from '../../../store/slices/settingsSlice'
import { FaFilePdf, FaFileWord, FaFileExcel, FaFileLines, FaFile } from 'react-icons/fa6'

type Props = {
  files: File[]
  pairs: PairInterface[]
  setFiles: React.Dispatch<SetStateAction<File[]>>
  setPairs: React.Dispatch<SetStateAction<PairInterface[]>>
  min_length: number
  length: number
  extensions: string[]
  maxVideoCount?: number
  minVideoCount?: number
  totalLength?: number
}

export const FileUpload = ({
  files,
  setFiles,
  pairs,
  setPairs,
  min_length,
  length = 3,
  extensions,
  maxVideoCount = 1,
  minVideoCount = 0,
  totalLength,
}: Props) => {
  const {
    drag,
    dragStartHandler,
    dragLeaveHandler,
    onDropHandler,
    selectFiles,
    errors,
    deletePreview,
  } = useFileUpload(
    files,
    setFiles,
    pairs,
    setPairs,
    min_length,
    length,
    extensions,
    maxVideoCount,
    minVideoCount,
    totalLength,
  )
  const dark = useSelector(selectDarkTheme)

  const renderPreview = (pair: PairInterface) => {
    const type = pair.type || 'image'

    if (type === 'video') {
      return (
        <video
          className={classes.PreviewVideo}
          src={pair.url}
          controls
          muted
          playsInline
          style={{ maxWidth: '100%', maxHeight: '150px' }}
        />
      )
    }

    if (type === 'document') {
      const ext = pair.file.name.split('.').pop()?.toLowerCase()
      let Icon = FaFile
      let color = '#757575'
      if (ext === 'pdf') {
        Icon = FaFilePdf
        color = '#e53935'
      }
      if (ext === 'doc' || ext === 'docx') {
        Icon = FaFileWord
        color = '#1e88e5'
      }
      if (ext === 'xls' || ext === 'xlsx') {
        Icon = FaFileExcel
        color = '#43a047'
      }
      if (ext === 'txt') {
        Icon = FaFileLines
        color = '#616161'
      }
      return (
        <div className={classes.DocumentPreview}>
          <Icon size={48} color={color} />
          <span className={classes.DocumentName}>{pair.file.name}</span>
        </div>
      )
    }

    return <img alt="" className={classes.PreviewImage} src={pair.url} />
  }

  const visiblePairs = pairs.filter((pair) => !pair.file.name.includes('poster_'))

  const imageCount = visiblePairs.filter((p) => p.type === 'image' || !p.type).length
  const videoCount = visiblePairs.filter((p) => p.type === 'video').length
  const totalCount = visiblePairs.length

  const isFull =
    (totalLength !== undefined && totalCount >= totalLength) ||
    (imageCount >= length && videoCount >= maxVideoCount)

  return (
    <div className={classes.DragContainer}>
      {drag === false ? (
        <div
          className={dark ? classes.DragZoneDark : classes.DragZone}
          style={{
            borderLeft: files.length < min_length ? ' solid 1px rgb(254, 111, 103,0.8)' : '',
          }}
          onDragStart={(e) => dragStartHandler(e)}
          onDragLeave={(e) => dragLeaveHandler(e)}
          onDragOver={(e) => dragStartHandler(e)}
          onDrop={(e) => onDropHandler(e)}
        >
          <div className={classes.mainText}>
            Добавьте файлы перетащив их в эту зону или кликом на кнопку
          </div>
          <label className={isFull ? classes.DragLabelError : classes.DragLabel}>
            Выберете файлы
            <input
              onChange={selectFiles}
              className={classes.DragInput}
              multiple
              type="file"
              name="files[]"
              disabled={isFull}
            />
          </label>
          <div className={classes.PreviewAnchor}>
            <div className={classes.PreviewContainer}>
              {visiblePairs.map((pair) => (
                <div key={pair.url} className={classes.ImageContainer}>
                  {renderPreview(pair)}
                  <div className={classes.DeletePreview} onClick={() => deletePreview(pair)}>
                    Удалить
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div
          className={classes.DragZoneDrop}
          onDragStart={(e) => dragStartHandler(e)}
          onDragLeave={(e) => dragLeaveHandler(e)}
          onDragOver={(e) => dragStartHandler(e)}
          onDrop={(e) => onDropHandler(e)}
        >
          Отпустите чтобы загрузить
        </div>
      )}
      <div className={classes.ErrorMessage}>
        {isFull ? `Добавлено максимальное количество файлов` : ``}
        {errors.min_quantity === true && errors.quantity === false && min_length
          ? `Загрузите хотя бы ${min_length} файл`
          : ''}
        {errors.videoCount && `Необходимо загрузить минимум ${minVideoCount} видео`}
        {errors.totalCount && `Можно загрузить не более ${totalLength} файлов`}
      </div>
    </div>
  )
}
